import { useState } from "react";
import { Select, Button, Card, message, Popconfirm, Modal, Input } from "antd";
import useCourseSkills from "./logic/useCourseSkills";
import "./CourseSkillsAssignment.scss";
import FlatTitle from "../../../LandingPage/components/ContactUs/components/FlatTitle";
import { PlusOutlined } from "@ant-design/icons";

const { Option } = Select;

const CourseSkillsAssignment = () => {
  const {
    courses,
    skills,
    assignSkills,
    isAssigning,
    coursesWithSkills,
    removeSkillFromCourse,
  } = useCourseSkills();

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedAdditionalSkills, setSelectedAdditionalSkills] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCourses = coursesWithSkills.filter((item) =>
    item.course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const [modalVisible, setModalVisible] = useState(false);

  const handleAssign = () => {
    if (!selectedCourse || selectedSkills.length === 0) {
      message.warning("Please select both course and skills");
      return;
    }

    assignSkills(
      { courseId: selectedCourse, skillIds: selectedSkills },
      {
        onSuccess: () => {
          message.success("Skills assigned to course successfully");
          setSelectedSkills([]);
          setSelectedCourse(null);
          setModalVisible(false);
        },
        onError: () => {
          message.error("Failed to assign skills");
        },
      }
    );
  };

  const handleRemoveSkill = (courseId, skillId) => {
    removeSkillFromCourse(
      { courseId, skillId },
      {
        onSuccess: () => {
          message.success("Skill removed");
        },
        onError: () => {
          message.error("Error removing skill");
        },
      }
    );
  };

  const handleAssignAdditionalSkills = (courseId) => {
    const skillsToAssign = selectedAdditionalSkills[courseId] || [];
    if (skillsToAssign.length === 0) {
      message.warning("Select skills to assign");
      return;
    }

    assignSkills(
      { courseId, skillIds: skillsToAssign },
      {
        onSuccess: () => {
          message.success("Additional skills assigned");
          setSelectedAdditionalSkills((prev) => ({ ...prev, [courseId]: [] }));
        },
        onError: () => {
          message.error("Failed to assign additional skills");
        },
      }
    );
  };

  return (
    <>
      <div className="btn-assign_modal">
        <Input.Search
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: "300px", marginRight: "10px" }}
          allowClear
        />
        <Button
          type="primary"
          onClick={() => setModalVisible(true)}
          icon={<PlusOutlined />}
        >
          Assign Skills to Course
        </Button>
      </div>

      <Modal
        title="Assign Skills to Course"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <div className="course-skills-assignment" bordered={false}>
          <div className="form-section">
            <Select
              showSearch
              placeholder="Select a course"
              onChange={setSelectedCourse}
              value={selectedCourse}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {courses.map((course) => (
                <Option key={course.id} value={course.id}>
                  {course.title}
                </Option>
              ))}
            </Select>

            <Select
              showSearch
              mode="multiple"
              placeholder="Select skills"
              onChange={setSelectedSkills}
              value={selectedSkills}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {skills.map((skill) => (
                <Option key={skill.id} value={skill.id}>
                  {skill.name}
                </Option>
              ))}
            </Select>

            <Button
              className="assign-button"
              type="primary"
              loading={isAssigning}
              onClick={handleAssign}
            >
              Assign Skills
            </Button>
          </div>
        </div>
      </Modal>

      {filteredCourses.length > 0 && (
        <div className="course-skills-overview">
          {filteredCourses.map((item) => (
            <div key={item.course.id} className="course-item">
              <div className="header-row">
                <h3>{item.course.title}</h3>
              </div>
              <div className="skills-list">
                {item.skills.map((skill) => (
                  <span className="skill-tag" key={skill.id}>
                    {skill.name}
                    <Popconfirm
                      title="Remove this skill?"
                      onConfirm={() =>
                        handleRemoveSkill(item.course.id, skill.id)
                      }
                      okText="Yes"
                      cancelText="No"
                    >
                      <span className="remove-icon">×</span>
                    </Popconfirm>
                  </span>
                ))}
              </div>
              <div className="assign-new-section">
                <Select
                  showSearch
                  mode="multiple"
                  placeholder="Add more skills"
                  style={{ width: "calc(100% - 120px)" }}
                  value={selectedAdditionalSkills[item.course.id] || []}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(values) =>
                    setSelectedAdditionalSkills((prev) => ({
                      ...prev,
                      [item.course.id]: values,
                    }))
                  }
                >
                  {skills
                    .filter(
                      (s) =>
                        !item.skills.find((assigned) => assigned.id === s.id)
                    )
                    .map((skill) => (
                      <Option key={skill.id} value={skill.id}>
                        {skill.name}
                      </Option>
                    ))}
                </Select>

                <Button
                  type="primary"
                  style={{ marginLeft: 8 }}
                  onClick={() => handleAssignAdditionalSkills(item.course.id)}
                >
                  Assign
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default CourseSkillsAssignment;
